import { useMutation, useQueryClient } from '@tanstack/react-query'

/** Group payload returned by the createGroup cloud function on success. */
export interface CreatedGroup {
  id: string
  groupName: string
  joinCode: string
}

/** Status envelope the createGroup callable resolves with (it does not throw on validation errors). */
export interface CreateGroupResponse {
  status: 'SUCCESS' | 'ERROR'
  message?: string
  group?: CreatedGroup
}

/**
 * The callable resolves a `{ status }` envelope rather than throwing on
 * failure, so unwrap it into either the created group or a thrown error the
 * mutation layer can surface.
 */
export function unwrapCreateGroupResponse(data: CreateGroupResponse): CreatedGroup {
  if (data.status !== 'SUCCESS' || !data.group) {
    throw new Error(data.message ?? 'Could not create the team. Please try again.')
  }
  return data.group
}

/** Landing tab when entering a freshly created team — Members, where the join
 *  code and "Invite members" live for a brand-new (creator-only) team. */
export const NEW_TEAM_LANDING_TAB = 'members' as const

/**
 * Navigation target for a just-created team (CLUB-180): straight into the new
 * team's detail on the Members tab. Kept pure so it's unit-testable.
 */
export function teamCreatedNavTarget(created: CreatedGroup) {
  return {
    to: '/teams/$teamId' as const,
    params: { teamId: created.id },
    search: { tab: NEW_TEAM_LANDING_TAB },
  }
}

/**
 * Creates a new team via the `createGroup` Firebase callable (deployed to
 * europe-west1), making the current user the owner. On success the
 * my-groups list is invalidated so the new team appears.
 *
 * This is the only callable the web invokes directly; everything else goes
 * through the REST BFF. The Functions SDK is imported lazily to keep it off
 * the synchronous bundle path.
 */
export function useCreateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (groupName: string): Promise<CreatedGroup> => {
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ groupName: string }, CreateGroupResponse>(fns, 'createGroup')
      const res = await call({ groupName })
      return unwrapCreateGroupResponse(res.data)
    },
    // Await the refetch so a caller that navigates into the new team on success
    // (CLUB-180) lands after the my-groups list already includes it — otherwise
    // the team-detail route briefly can't find the team and flashes "no access".
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
